import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, BadgeCheck } from "lucide-react";

const resolveUrl = (u) => {
  if (!u) return "/coming-soon.svg";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const onImgError = (e) => {
  if (e.target.src.indexOf("coming-soon.svg") === -1) {
    e.target.src = "/coming-soon.svg";
  }
};

export default function CompanionCard({ c, index = 0 }) {
  const photos = (c.photos && c.photos.length > 0 ? c.photos : [c.cover_photo]).filter(Boolean);
  const [photoIdx, setPhotoIdx] = useState(0);
  const intervalRef = useRef(null);

  const startCarousel = () => {
    if (photos.length <= 1) return;
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      setPhotoIdx((i) => (i + 1) % photos.length);
    }, 1400);
  };
  const stopCarousel = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPhotoIdx(0);
  };

  useEffect(() => () => intervalRef.current && clearInterval(intervalRef.current), []);

  return (
    <Link
      to={`/companions/${c.id}`}
      data-testid={`companion-card-${c.id}`}
      className="companion-card group block fade-up"
      style={{ animationDelay: `${(index % 8) * 70}ms` }}
      onMouseEnter={startCarousel}
      onMouseLeave={stopCarousel}
    >
      <div className="photo relative overflow-hidden">
        {photos.map((p, i) => (
          <img
            key={p}
            src={resolveUrl(p)}
            alt={c.name}
            loading="lazy"
            onError={onImgError}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: i === photoIdx ? 1 : 0 }}
          />
        ))}
        {/* keep aspect spacer */}
        <img
          src={resolveUrl(photos[0])}
          alt=""
          aria-hidden="true"
          onError={onImgError}
          className="opacity-0 w-full block"
        />

        {/* BILLION EURO BABIES watermark */}
        <div className="beb-watermark" aria-hidden="true">BILLION EURO BABIES</div>

        {/* Photo dots indicator (only when hovering & >1 photo) */}
        {photos.length > 1 && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10">
            {photos.map((p, i) => (
              <span
                key={p}
                className={`h-[2px] transition-all duration-500 ${
                  i === photoIdx ? "w-6 bg-[var(--gold)]" : "w-3 bg-white/40"
                }`}
              />
            ))}
          </div>
        )}

        {c.verified && (
          <div className="absolute top-3 left-3 flex items-center gap-1 bg-black/70 backdrop-blur px-2.5 py-1 text-[0.6rem] tracking-[0.2em] uppercase text-[var(--gold)] z-10">
            <BadgeCheck size={11} /> Verified
          </div>
        )}

        {c.featured && (
          <div className="absolute top-3 right-3 z-10 vip-stamp">
            <div className="vip-stamp-inner">VIP</div>
            <svg
              className="vip-stamp-ring"
              viewBox="0 0 100 100"
              aria-hidden="true"
            >
              <defs>
                <path
                  id={`vip-ring-${c.id}`}
                  d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0"
                />
              </defs>
              <text className="vip-ring-text">
                <textPath href={`#vip-ring-${c.id}`} startOffset="0">
                  ◆ EXCLUSIVE ◆ PRIVATE ◆ VIP ◆ LUXURY 
                </textPath>
              </text>
            </svg>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
        <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end z-10">
          <div>
            <div className="font-serif-display text-2xl companion-name-luxury leading-none">
              {c.name}
            </div>
            <div className="text-white/60 text-xs tracking-wider mt-1 flex items-center gap-1.5">
              <span>{c.age}</span>
              <span className="text-[var(--gold)]">·</span>
              <span>{c.nationality}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="price-big font-serif-display text-xl">€{c.price}</div>
            <div className="text-white/40 text-[0.6rem] tracking-widest uppercase">
              /hour
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 flex items-center justify-between text-white/50 text-[0.7rem] tracking-widest uppercase border-t border-white/5">
        <span className="flex items-center gap-1.5">
          <MapPin size={11} /> {c.location}
        </span>
        <span className="rose-gold-text">View Profile →</span>
      </div>
    </Link>
  );
}
